// ============================================================
// contactDiff.ts — (client-sync batch) the case's copy of the client's
// CONTACT details (email, phone, postal address) vs the client record (pure;
// contactDiff.test.ts). Built on clientRecordDiff (same comparison rules: blank
// ≡ blank, trimmed, postcodes compared without spaces / case).
// Bills, credit notes, statements and the consent check use the client record,
// so a case copy that differs is flagged (ContactMismatchNote) with two
// actions — one request each (POST /api/cases/:id/contact-sync): "Update the
// client record" (the case values onto the record) or "Use the client
// record’s …" (the record values onto this case).
// The groups match backend caseController CONTACT_GROUPS.
// ============================================================
import { clientRecordChanges, comparable, type ClientRecordField } from '../components/sections/clientRecordDiff'
import type { RawCase } from '../types/case.types'

export const CONTACT_GROUPS = {
  email: ['emailAddress', 'hasEmailAddress'],
  phone: ['mainContactNumber'],
  address: ['addressLine1', 'addressLine2', 'addressTown', 'addressCounty', 'addressPostcode'],
} as const satisfies Record<string, readonly ClientRecordField[]>
export type ContactGroup = keyof typeof CONTACT_GROUPS
export const CONTACT_GROUP_KEYS = Object.keys(CONTACT_GROUPS) as ContactGroup[]
const LABEL: Record<ContactGroup, string> = { email: 'email', phone: 'phone number', address: 'address' }
export const CONTACT_FIELDS: readonly ClientRecordField[] = CONTACT_GROUP_KEYS.flatMap((g) => CONTACT_GROUPS[g])
export const contactGroupOf = (field: string): ContactGroup | null =>
  CONTACT_GROUP_KEYS.find((g) => (CONTACT_GROUPS[g] as readonly string[]).includes(field)) ?? null

type Side = Record<string, unknown>
// One group's value as a person reads it.
const shown = (g: ContactGroup, s: Side): string => {
  if (g === 'email') return s.hasEmailAddress === false ? 'no email address' : String(s.emailAddress ?? '').trim() || '(blank)'
  if (g === 'phone') return String(s.mainContactNumber ?? '').trim() || '(blank)'
  return CONTACT_GROUPS.address.map((f) => String(s[f] ?? '').trim()).filter(Boolean).join(', ') || '(blank)'
}

export interface ContactDiff {
  group: ContactGroup
  label: string
  caseValue: string
  recordValue: string
  // Each side's own values for the group — what the user was shown (sent so
  // the server can tell a real change from a version bump).
  recordFields: Record<string, unknown>
  caseFields: Record<string, unknown>
}
export const contactDiffs = (caseCopy: Side | null | undefined, record: Side | null | undefined): ContactDiff[] => {
  if (!caseCopy || !record) return []
  const changed = new Set(clientRecordChanges(caseCopy, record).map((c) => c.field as string))
  return CONTACT_GROUP_KEYS
    .filter((g) => CONTACT_GROUPS[g].some((f) => changed.has(f)))
    // (an email that differs only in letter case isn't a different address)
    .filter((g) => g !== 'email' || caseCopy.hasEmailAddress !== record.hasEmailAddress || comparable('emailAddress', caseCopy.emailAddress).toLowerCase() !== comparable('emailAddress', record.emailAddress).toLowerCase())
    .map((g) => ({
      group: g, label: LABEL[g], caseValue: shown(g, caseCopy), recordValue: shown(g, record),
      recordFields: Object.fromEntries(CONTACT_GROUPS[g].map((f) => [f, record[f] ?? null])),
      caseFields: Object.fromEntries(CONTACT_GROUPS[g].map((f) => [f, caseCopy[f] ?? null])),
    }))
}
// The calm note's sentence + its second button.
export const mismatchMessage = (d: Pick<ContactDiff, 'label' | 'caseValue' | 'recordValue'>): string =>
  `This case’s ${d.label} is ${d.caseValue}; the client record has ${d.recordValue}. Bills go to the client record.`
export const keepRecordLabel = (d: Pick<ContactDiff, 'label'>): string => `Use the client record’s ${d.label}`

// The two sides from a case as the API returns it (case columns client* vs the linked client).
export const contactSidesOfRaw = (c: RawCase): { caseCopy: Side; record: Side | null; clientUpdatedAt: string | null } => {
  const r = c as unknown as Record<string, unknown> & { client?: Record<string, unknown> | null }
  return {
    caseCopy: {
      emailAddress: r.clientEmailAddress, hasEmailAddress: r.clientHasEmailAddress, mainContactNumber: r.clientMainContactNumber,
      addressLine1: r.clientAddressLine1, addressLine2: r.clientAddressLine2, addressTown: r.clientAddressTown, addressCounty: r.clientAddressCounty, addressPostcode: r.clientAddressPostcode,
    },
    record: r.client ?? null,
    clientUpdatedAt: typeof r.client?.updatedAt === 'string' ? r.client.updatedAt : null,
  }
}
